import { Fragment, useCallback, useEffect, useState } from "react";
import { css } from "@emotion/react";
import { QRCodeSVG } from "qrcode.react";

import useForm from "../../../hooks/useForm";
import { FLAG_ZITADEL_MFA } from "../../../includes/featureFlags";
import { hasFeatureFlag } from "../../../includes/site";
import { useAppState } from "../../../contexts/AppContext";
import { AUTH_URL, getUrl, zitadelRoutes } from "../../../includes/routes";
import Button from "../../Button";
import Form from "../../form/Form";
import LabelledField from "../../form/LabelledField";
import Input from "../../form/Input";
import FormActions from "../../form/FormActions";
import { useZitadel } from "../../../contexts/ZitadelContext";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { FAILED_AUTHENTICATION_PARAM, zitadelGraphqlErrors } from "../../../includes/constants";
import { API_BASE_URL } from "../../../../config/config";

type ZitadelCompleteTotpChallengeMutationResponse = {
    zitadelCompleteTotpChallenge: {
        id: string
    };
}

type ZitadelCompleteTotpChallengeMutationVariables = {
    sessionId: string;
    sessionToken: string;
    code: string;
}

const TotpChallenge = () => {
    const navigate = useNavigate();

    const [isTotpLoading, setIsTotpLoading] = useState(false);
    const [isCancelLoading, setIsCancelLoading] = useState(false);
    const [isFallbackRedirectLoading, setIsFallbackRedirectLoading] = useState(false);

    const [appState] = useAppState();
    const isFeatureFlagsLoading = appState.isFeatureFlagsLoading;
    const hasZitadelMfaFlag = hasFeatureFlag(appState.featureFlags, FLAG_ZITADEL_MFA);

    const {
        loginInfo,
        setLoginInfo,
        clearLoginInfo,
        session,
        clearSession,
        totpRegistration,
        clearTotpRegistration,
    } = useZitadel();

    const { formState, onFieldChange, onFieldInvalid, formErrors, setFormErrors } = useForm({
        fields: { code: "" },
    });

    const [zitadelCompleteTotpChallenge] =
        useMutation<ZitadelCompleteTotpChallengeMutationResponse, ZitadelCompleteTotpChallengeMutationVariables>(
            ZITADEL_COMPLETE_TOTP_CHALLENGE,
        );

    const onSubmit = useCallback(() => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register TOTP.");

        setIsTotpLoading(true);

        zitadelCompleteTotpChallenge({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
                code: formState.code,
            },
            fetchPolicy: 'network-only',
        }).then(() => {
            clearLoginInfo();
            clearSession();
            clearTotpRegistration();
            window.location.href = `${API_BASE_URL}/auth/login-redirect`;
        }).catch((e) => {
            const graphQlErrors = e.graphQLErrors;
            if (graphQlErrors) {
                if (
                    graphQlErrors.find(
                        (graphQlError: any) => graphQlError.extensions?.details?.includes(
                            zitadelGraphqlErrors.INVALID_MFA_CODE
                        )
                    )
                ) {
                    setFormErrors({ code: "Invalid code" });
                    // Screen reader announces a field error message.
                    const firstTargetKey = Object.keys(formErrors)?.[0] ?? null;
                    if (firstTargetKey) {
                        const firstElement = document.getElementsByName(Object.keys(formErrors)[0]);
                        if (firstElement.length > 0) {
                            firstElement[0].focus();
                        }
                    }
                    return;
                }
            }
            clearLoginInfo();
            clearSession();
            clearTotpRegistration();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }, [
        clearLoginInfo,
        clearSession,
        clearTotpRegistration,
        formErrors,
        formState.code,
        navigate,
        session,
        setFormErrors,
        zitadelCompleteTotpChallenge,
    ]);

    const onCancel = () => {
        setIsCancelLoading(true);
        setLoginInfo(prev => prev ? { ...prev, isTotpChallengeRequired: false } : null);
        clearTotpRegistration();

        if (loginInfo?.isMfaRegistrationRequired) {
            /** Redirect to MFA initialisation if its still necessary */
            navigate(getUrl(zitadelRoutes.MFA_INITIALISATION.route));
        } else {
            /** Redirect to start of login flow otherwise */
            navigate(getUrl(AUTH_URL));
        }
    }

    useEffect(function fallbackRedirection() {
        /** SKip fallback redirection if actions are loading since the actions do the redirection themselves */
        if (isTotpLoading || isCancelLoading) return;

        if (!isFeatureFlagsLoading && !hasZitadelMfaFlag) {
            /** Redirect to start of login flow if missing required feature flags */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(AUTH_URL));
        }  else if (!loginInfo || !session) {
            /** Redirect to start of login flow if missing required variables */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(AUTH_URL));
        } else if (!loginInfo.isTotpChallengeRequired) {
            /** Redirect if challenge is not required */
            if (loginInfo.isMfaRegistrationRequired) {
                /** Redirect to MFA initialisation if its still necessary */
                setIsFallbackRedirectLoading(true);
                navigate(getUrl(zitadelRoutes.MFA_INITIALISATION.route));
            } else {
                /** Redirect to start of login flow otherwise */
                setIsFallbackRedirectLoading(true);
                navigate(getUrl(AUTH_URL));
            }
        } else if (loginInfo.isMfaRegistrationRequired && !totpRegistration) {
            /** Redirect to start of login flow if missing required variables during registration */
            setIsFallbackRedirectLoading(true);
            navigate(getUrl(AUTH_URL));
        }
    }, [
        hasZitadelMfaFlag,
        isCancelLoading,
        isFeatureFlagsLoading,
        isTotpLoading,
        loginInfo,
        navigate,
        session,
        totpRegistration,
    ]);

    const isLoading = isFeatureFlagsLoading || isFallbackRedirectLoading;
    const isActionsDisabled = isTotpLoading || isCancelLoading || isLoading;

    /** Blocking based on loginInfo & session is not actually necessary, but helps with typescript hinting */
    if (isLoading || !loginInfo || !session) return <div>loading...</div>;

    return (
        <div css={authContentStyle}>
            <div css={headerStyle}>
                <h1 css={leftHeaderStyle}>Multi-Factor Authentication</h1>
            </div>
            {loginInfo.isMfaRegistrationRequired && totpRegistration && (
                <Fragment>
                    <p>Each time you log in, you will use your authenticator app and enter a one-time code.</p>
                    <h2>Step 1: Set up authenticator app</h2>
                    <p>Scan the QR code with your authenticator app (like Google Authenticator or Authy):</p>
                    <div css={totpRegistrationStyle}>
                        <QRCodeSVG css={qrCodeStyle} aria-label="TOTP QR code" value={totpRegistration.uri} />
                        <div css={manualCodeStyle}>
                            If you can't scan the code, enter the secret key manually
                            <br />
                            <code aria-label="TOTP secret">{totpRegistration.secret}</code>
                        </div>
                    </div>
                    <p>
                        Alternatively, if your device supports it you can{" "}
                        <a href={totpRegistration.uri}>follow this link to register with your authenticator</a>.
                    </p>
                </Fragment>
            )}
            {loginInfo.isMfaRegistrationRequired && <h2>Step 2: Get verification code</h2>}
            <p>Enter the 6-digit code you see in your authenticator app.</p>
            <Form onSubmit={onSubmit}>
                <LabelledField
                    name="code"
                    error={formErrors.code}
                    label="Verification code"
                    version={'normal'}
                >
                    <Input
                        hasError={!!formErrors.code}
                        label="000000"
                        name="code"
                        onChange={onFieldChange}
                        onInvalid={onFieldInvalid}
                        value={formState.code}
                        hasBorder
                        required
                    />
                </LabelledField>
                <div css={formActionsStyle}>
                    <FormActions customCss={customFormActionsStyles}>
                        {loginInfo?.isMfaRegistrationRequired && (
                            <Button
                                css={cancelButtonStyle}
                                type="button"
                                onClick={onCancel}
                                disabled={isActionsDisabled}
                                forceLoading={isCancelLoading}
                                useOutline
                            >
                                Choose another method
                            </Button>
                        )}
                        <Button
                            css={buttonStyle}
                            type="submit"
                            disabled={isActionsDisabled}
                            forceLoading={isTotpLoading}
                            useOutline
                        >
                            Confirm
                        </Button>
                    </FormActions>
                </div>
            </Form>
        </div>
    );
}

const ZITADEL_COMPLETE_TOTP_CHALLENGE = gql`
    mutation zitadelCompleteTotpChallenge($sessionId: String!, $sessionToken: String!, $code: String!) {
        zitadelCompleteTotpChallenge(sessionId: $sessionId, sessionToken: $sessionToken, code: $code) {
            id
        }
    }
`;

// Copied from SignIn.tsx
const authContentStyle = (theme: EmotionTheme) => css`
    margin-top: 40px;
    width: 510px;
    position: relative;

    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }

    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }
`;

// Copied from SignIn.tsx
const headerStyle = css`
    display: flex;
    justify-content: space-between;
`;

// Copied from SignIn.tsx
const leftHeaderStyle = (theme: EmotionTheme) => css`
    margin: 0;
    color: ${theme.colours.curiousBlue};
    font-weight: ${theme.fonts.weights.bold};

    ${theme.breakpoints.up("xxl")} {
        font-size: 50px;
        line-height: 75px;
    }

    ${theme.breakpoints.between("md", "xl")} {
        font-size: 43px;
        line-height: 65px;
    }

    ${theme.breakpoints.down("sm")} {
        font-size: 30px;
        line-height: 45px;
    }
`;

// Copied from SignIn.tsx
const formActionsStyle = (theme: EmotionTheme) => css`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    ${theme.breakpoints.up('sm')} {
        margin-top: 20px;
        justify-content: center;
    }

    ${theme.breakpoints.up("5xl")} {
        margin: 63px 0 68px;
    }

    ${theme.breakpoints.between("xxl", "4xl")} {
        margin: 41px 0 45px;
    }

    ${theme.breakpoints.between("lg", "xl")} {
        margin: 29px 0 34px;
    }

    ${theme.breakpoints.only("md")} {
        margin: 21px 0 26px;
    }

    ${theme.breakpoints.only("sm")} {
        margin: 26px 0 31px;
    }

    ${theme.breakpoints.down("xs")} {
        margin: 19px 0 24px;
    }
`;

// Copied from SignIn.tsx
const customFormActionsStyles = css`
    width: 100%;
    justify-content: center;
`;

// Copied from SignIn.tsx
const buttonStyle = (theme: EmotionTheme) => css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;
const cancelButtonStyle = (theme: EmotionTheme) => css`
    ${buttonStyle(theme)};
    background: none;
    color: ${theme.colours.blue['400']};
    border: 1px solid ${theme.colours.blue['400']};
`;

const totpRegistrationStyle = (theme: EmotionTheme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    margin-bottom: 20px;
    background-color: ${theme.colours['grey']['100']};
    border-radius: 16px;
`;

const qrCodeStyle = css`
    border: solid 0.5rem white;
    background-color: white;
`;

const manualCodeStyle = css`
    text-align: center;
`;

export default TotpChallenge;
